import { Component } from '@angular/core';
import { PageEvent } from '@angular/material/paginator';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Company } from 'identity-webapi-client';
import { GetIfcSettingsRequest, IfcToJsonSettings, SaveIfcSettingsRequest, SituWarpApiClient, IfcToJsonRoomSettings, IIfcToJsonRoomSettings, RoomType } from 'situ-warpapi-client';
import { AuthService, BreadcrumbService, StateService, BannerService, BannerServiceStatus } from 'src/app/services';
import { SituBaseComponent } from 'src/app/shared/situ-base-component';
import { Guid } from 'guid-typescript';

@Component({
  selector: 'app-ifc-rooms-config',
  templateUrl: './ifc-rooms-config.component.html',
  styleUrls: ['./ifc-rooms-config.component.less'],
})
export class IfcRoomsConfigComponent extends SituBaseComponent {
  selectedCompany: Company | undefined;
  roomTypeEnum = RoomType;
  roomTypeKeyValues = Object.keys(RoomType).filter(x => typeof RoomType[x as any] === "number").map(key => ({ key: key, value: (RoomType as any)[key] })).sort((a, b) => a.key.localeCompare(b.key));

  settings: IfcToJsonSettings;
  rooms: string[] = [];

  isNewRoomFeed = true;
  roomSettings: IfcToJsonRoomSettings[] = [];
  newRoomSettings: ExtendedIfcToJsonRoomSettings[] = [];
  roomSettingsPageIndex = 0;
  pagedRoomSettings: IfcToJsonRoomSettings[] = [];
  newRoomSettingsPageIndex = 0;
  pagedNewRoomSettings: ExtendedIfcToJsonRoomSettings[] = [];

  artifactId: Guid | undefined;

  readonly pageSize = 200;

  private _loading = false;
  get loading() {
    return this._loading;
  }
  set loading(val: boolean) {
    this._loading = val;
    this.breadcrumbService.setIsLoading(this._loading);
  }

  get settingsjson() {
    return JSON.stringify(this.settings);
  }

  get isRoomSettingsDirty() {
    return JSON.stringify(this.settings.roomSettings) != JSON.stringify(this.roomSettings) || this.newRoomSettings.some(x => x.roomType !== undefined);
  }

  constructor(
    authService: AuthService,
    stateService: StateService,
    private warpApiClient: SituWarpApiClient,
    private breadcrumbService: BreadcrumbService,
    private snackBar: MatSnackBar,
    private bannerService: BannerService) {
      super(authService, stateService);
    this.settings = this.getNewSettings();
  }

  protected onSelectedCompanyChange(): void {
    this.loadSettings(this.selectedCompany?.code!);
  }

  loadSettings(companyCode: string) {
    const request = { companyCode: companyCode, includeRooms: true } as GetIfcSettingsRequest;
    this.loading = true;
    this.warpApiClient.getIfcSettings(request).then((response) => {
      if (!response.success) {
        this.snackBar.open("Failed to load.");
      }

      this.artifactId = Guid.parse(response.artifactId!);

      const newSettings = this.getNewSettings();
      let settings = response.settings || newSettings;
      if (!settings.roomSettings) {
        settings.roomSettings = newSettings.roomSettings;
      }
      if (!settings.buildingMaterialSettings) {
        settings.buildingMaterialSettings = newSettings.buildingMaterialSettings;
      }
      if (!settings.storeySettings) {
        settings.storeySettings = newSettings.storeySettings;
      }
      if (!settings.furnishingSettings) {
        settings.furnishingSettings = newSettings.furnishingSettings;
      }

      this.settings = settings;
      this.roomSettings = JSON.parse(JSON.stringify(settings.roomSettings))?.map((x: any) => new IfcToJsonRoomSettings(x)) || [];

      this.pagedRoomSettings = this.paginate(this.roomSettings || [], this.pageSize, this.roomSettingsPageIndex);

      this.rooms = response.rooms || [];

      const newRooms = this.rooms.filter((room) => !!room && !this.roomSettings.some(y => y.roomName == room)) || [];

      this.newRoomSettings = newRooms.map((room) => new ExtendedIfcToJsonRoomSettings({ roomName: room, suggestedRoomType: this.getSuggestedRoomType(room) }));
      this.pagedNewRoomSettings = this.paginate(this.newRoomSettings, this.pageSize, this.newRoomSettingsPageIndex);

      this.loading = false;
    }).catch((e) => {
      this.loading = false;
      this.snackBar.open("Failed to load.");
    });
  }

  getNewSettings() {
    let settings = new IfcToJsonSettings();
    settings.builder = this.selectedCompany?.code;
    settings.roomSettings = [];
    return settings;
  }

  onRoomsPageChange(event: PageEvent) {
    this.roomSettingsPageIndex = event.pageIndex;
    this.pagedRoomSettings = this.paginate(this.roomSettings, this.pageSize, this.roomSettingsPageIndex);
  }
  onNewRoomsPageChange(event: PageEvent) {
    this.newRoomSettingsPageIndex = event.pageIndex;
    this.pagedNewRoomSettings = this.paginate(this.newRoomSettings, this.pageSize, this.newRoomSettingsPageIndex);
  }

  toggleNewRoomSettings() {
    this.isNewRoomFeed = !this.isNewRoomFeed;
  }

  removeRoomSetting(roomSetting: IfcToJsonRoomSettings) {
    const index = this.roomSettings!.indexOf(roomSetting);
    let removedItems = this.roomSettings!.splice(index, 1);

    let removedExtendedItems = removedItems.map((roomSetting) =>
      new ExtendedIfcToJsonRoomSettings({ roomName: roomSetting.roomName, suggestedRoomType: this.getSuggestedRoomType(roomSetting.roomName || "") }))
    this.newRoomSettings.push(...removedExtendedItems);

    this.pagedRoomSettings = this.paginate(this.roomSettings, this.pageSize, this.roomSettingsPageIndex);
    this.pagedNewRoomSettings = this.paginate(this.newRoomSettings, this.pageSize, this.newRoomSettingsPageIndex);

    this.snackBar.open("Removed room setting!");
  }

  useSuggestedRoomType(roomSetting: ExtendedIfcToJsonRoomSettings) {
    roomSetting.roomType = roomSetting.suggestedRoomType;
  }

  getSuggestedRoomType(room: string): RoomType | undefined {
    return this.findKeyMatch(room, this.roomTypeKeyValues, suggestedRoomTypeDictionary);
  }

  findKeyMatch(text: string, keyValues?: { key: string, value: any }[], dictionary?: { key: RegExp, value: any }[]): any {
    let results: { key: string | RegExp, value: any }[];
    // See if the supplied dictionary RegEx has any hits
    results = dictionary?.filter(x => x.key.test(text)) || [];
    if (results.length) {
      return results[0].value;
    }

    const searchText = text.toLowerCase().replace(/[0-9]/g, '').replace(' ', '').trim();
    if (searchText.length <= 3) {
      // Don't try and do a generic match on such a short string
      return;
    }

    // See if any of the type enums directly match the searchText
    results = keyValues?.filter(x => x.key.toLowerCase().trim() == searchText) || [];
    if (results.length) {
      return results[0].value;
    }

    // See if any of the type enums contains the searchText
    results = keyValues?.filter(x => x.key.toLowerCase().trim().indexOf(searchText) >= 0) || [];
    if (results.length) {
      return results[0].value;
    }

    // See if the searchText contains any of the type enums
    results = keyValues?.filter(x => searchText.indexOf(x.key.toLowerCase().trim()) >= 0) || [];
    if (results.length) {
      return results[0].value;
    }
  }

  getEnumKeyForDisplay(key: string) {
    if (key == null) {
      return '';
    }

    let display = key.replace(/([A-Z])/g, ' $1').trim();
    let words = display.split(' ');
    display = '';
    for (let i = 0; i < words.length; i++) {
      const word = words[i];
      const nextWord = words[i + 1];
      display += word;
      if (word.length > 1 || nextWord?.length > 1) {
        display += ' ';
      }
    }

    display = display.trim();
    return display;
  }

  paginate(array: any[], pageSize: number, pageIndex: number) {
    return array.slice(pageIndex * pageSize, (pageIndex * pageSize) + pageSize);
  }

  saveRoomSettings() {
    if (!this.settings) {
      return;
    }

    const request = { latestArtifactId: this.artifactId!.toString(), settings: new IfcToJsonSettings({ builder: this.settings.builder }) } as SaveIfcSettingsRequest;
    request.settings!.roomSettings = this.roomSettings.concat(this.newRoomSettings).filter(x => x.roomType !== undefined) || [];
    request.settings!.buildingMaterialSettings = this.settings.buildingMaterialSettings;
    request.settings!.storeySettings = this.settings.storeySettings;
    request.settings!.furnishingSettings = this.settings.furnishingSettings;

    this.loading = true;
    this.warpApiClient.saveIfcSettings(request).then((response) => {
      if (!response.success) {
        if (response.errorMessages && response.errorMessages.length > 0) {
          var message = `${response.errorMessages[0]}<br /><br />Please refresh the page and re-enter your changes again.`
          this.bannerService.AddMessage(message, BannerServiceStatus.error, this.artifactId, true);
        }
        this.snackBar.open("Failed to save room settings.");
      }
      else {
        this.bannerService.RemoveMessage(this.artifactId!);
        this.snackBar.open("Saved room settings!");

        this.artifactId = Guid.parse(response.artifactId!);

        this.settings.roomSettings = JSON.parse(JSON.stringify(request.settings?.roomSettings))?.map((x: any) => new IfcToJsonRoomSettings(x)) || [];
        this.roomSettings = request.settings?.roomSettings || [];

        this.pagedRoomSettings = this.paginate(this.roomSettings || [], this.pageSize, this.roomSettingsPageIndex);

        this.newRoomSettings = this.newRoomSettings.filter((room) => !this.roomSettings.some(y => y.roomName == room.roomName)) || [];

        if (this.newRoomSettings.length > 0 && this.newRoomSettings.length < (this.newRoomSettingsPageIndex * this.pageSize) + 1) {
          this.newRoomSettingsPageIndex = Math.floor(this.newRoomSettings.length / this.pageSize) - 1;
        }
        this.pagedNewRoomSettings = this.paginate(this.newRoomSettings, this.pageSize, this.newRoomSettingsPageIndex);
      }
      this.loading = false;
    }).catch(() => {
      this.loading = false;
      this.snackBar.open("Failed to save room settings.");
    });
  }
}

class ExtendedIfcToJsonRoomSettings extends IfcToJsonRoomSettings {
  suggestedRoomType?: RoomType;

  constructor(data?: IExtendedIfcToJsonRoomSettings) {
    super(data);
  }
}
interface IExtendedIfcToJsonRoomSettings extends IIfcToJsonRoomSettings {
  suggestedRoomType?: RoomType;
}

// A collection of suggested room types
// Add the most specific matches at the top e.g. MasterBedroom above Bedroom
// Simple equals and contains matches for suitably sized strings are done later if the test fails to match e.g. 'Bedroom 2' matches Bedroom and 'kids area' matches Kids
const suggestedRoomTypeDictionary = [
  { key: /bed.*1/i, value: RoomType.MasterBedroom },
  { key: /^(?=.*(\bmeals\b|\bdining\b))(?=.*\bliving\b).*/i, value: RoomType.MealsAndLiving },
  { key: /^(?=.*(\bmeals\b|\bdining\b))(?=.*\bfamily\b).*/i, value: RoomType.MealsAndFamily },
  { key: /^(?=.*(\bmeals\b|\bdining\b))(?=.*\brumpus\b).*/i, value: RoomType.MealsAndRumpus },
  { key: /\bbed|guest/i, value: RoomType.Bedroom },
  { key: /\bl'?dry?\b/i, value: RoomType.Laundry },
  { key: /lounge/i, value: RoomType.Family },
  { key: /dining/i, value: RoomType.Meals },
  { key: /\bkid|\bchild/i, value: RoomType.Kids },
  { key: /lounge|games|retreat/i, value: RoomType.Living },
  { key: /theatre|theater/i, value: RoomType.Media },
  { key: /\bens\b/i, value: RoomType.Ensuite },
  { key: /\bw\.?i\.?l\b/i, value: RoomType.WalkInLinen },
  { key: /\blin\b|\blc\b/i, value: RoomType.Linen },
  { key: /\bw\.?i\.?p\b|\bp'?tr?y\b|butler/i, value: RoomType.Pantry },
  { key: /\bw\.?i\.?r\b/i, value: RoomType.Robe },
  { key: /office|nook|library|retreat/i, value: RoomType.Study },
  { key: /p'?dr|wc/i, value: RoomType.WC },
  { key: /pergola|outdoor/i, value: RoomType.Alfresco },
  { key: /verandah|pergola|\bcourt|\bpor|\bmud\b/i, value: RoomType.Porch },
  { key: /carport|workshop/i, value: RoomType.Garage },
  { key: /\bbalc/i, value: RoomType.Balcony },
  { key: /entry/i, value: RoomType.Passage },
  { key: /\bstair/i, value: RoomType.Stairwell },
];
